// Invoices view for the portal — list with expandable line items and
// the Pay button. Queries portal_invoices (the view), NEVER raw
// invoices.
//
// Pay button visibility:
//   render IFF stripe_payment_link_url IS NOT NULL AND status NOT IN
//   ('paid','void'). Unpayable invoices (no link or paid/void) show
//   the amount due but no button. The link opens in a new tab; no
//   client-side payment state mutation — Stripe drives the status flip
//   via webhook back to the staff app.
//
// Status colors mirror the operator-side Invoices.tsx STATUS_CONFIG
// (inlined here rather than extracted to a shared helper — keeping
// scope to this task, not refactoring the staff page).
//
// Line items: see header of PortalQuotes.tsx — same recon, no internal
// fields in the JSONB.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate } from '@/lib/utils'
import { lineTotal, pricingBands, type PricingType, type PricingTier } from '@/lib/totals'
import { ChevronDown, ChevronUp, Receipt, ExternalLink, Download, Loader2 } from 'lucide-react'
import { downloadPortalPdf } from '@/lib/portalPdf'
import { toast } from 'sonner'
import { PortalSkeletonList } from '@/pages/portal/PortalSkeleton'

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  draft:   { label: 'Draft',   color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)', border: 'var(--border-default)' },
  sent:    { label: 'Sent',    color: 'var(--info)',         bg: 'var(--info-dim)',      border: 'var(--info-border)' },
  viewed:  { label: 'Viewed',  color: 'var(--accent-text)',  bg: 'var(--accent-dim)',    border: 'var(--accent-border)' },
  paid:    { label: 'Paid',    color: 'var(--positive)',     bg: 'var(--positive-dim)',  border: 'var(--positive-border)' },
  partial: { label: 'Partial', color: 'var(--warning)',      bg: 'var(--warning-dim)',   border: 'var(--warning-border)' },
  overdue: { label: 'Overdue', color: 'var(--danger)',       bg: 'var(--danger-dim)',    border: 'var(--danger-border)' },
  void:    { label: 'Void',    color: 'var(--ink-tertiary)', bg: 'var(--hover-bg)',      border: 'var(--border-subtle)' },
}

interface InvoiceLineItem {
  id: string | number
  name: string
  description?: string | null
  qty: number
  unit?: string | null
  unit_price: number
  total: number
  // Arc B Slice 2 — present in the JSONB for tiered/volume lines (CP3 renders
  // the breakdown). Missing/'flat' → qty × unit_price.
  pricing_type?: PricingType | null
  tiers?: PricingTier[] | null
  // Arc B Slice 3 — per-line modifiers; the portal shows "× surcharge" / "+ fee".
  line_fee?: number | null
  price_multiplier?: number | null
}

interface PortalInvoice {
  id: string
  invoice_number: string
  status: string
  line_items: InvoiceLineItem[] | null
  subtotal: number | null
  discount_amount: number | null
  tax_rate: number | null
  tax_amount: number | null
  total: number | null
  amount_paid: number | null
  amount_due: number | null
  currency: string | null
  issue_date: string | null
  due_date: string | null
  payment_terms: string | null
  notes: string | null
  footer_text: string | null
  stripe_payment_link_url: string | null
  sent_at: string | null
  paid_at: string | null
  created_at: string | null
  // Billing presentation CP3: portal render hides qty/rate/bands/modifiers when true.
  display_amount_only: boolean | null
}

export default function PortalInvoices() {
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['portal-invoices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('portal_invoices')
        .select('id, invoice_number, status, line_items, subtotal, discount_amount, tax_rate, tax_amount, total, amount_paid, amount_due, currency, issue_date, due_date, payment_terms, notes, footer_text, stripe_payment_link_url, sent_at, paid_at, created_at, display_amount_only')
        .order('created_at', { ascending: false, nullsFirst: false })
      if (error) throw error
      return (data ?? []) as PortalInvoice[]
    },
  })

  if (isLoading) {
    return <PortalSkeletonList count={3} height={130} />
  }
  if (invoices.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <Receipt style={{ width: 28, height: 28, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>No invoices yet</p>
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Issued invoices, payment links, and PDF downloads will live here.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {invoices.map(inv => <PortalInvoiceCard key={inv.id} invoice={inv} />)}
    </div>
  )
}

function PortalInvoiceCard({ invoice }: { invoice: PortalInvoice }) {
  const [expanded, setExpanded] = useState(false)
  const cfg = STATUS_CONFIG[invoice.status] ?? STATUS_CONFIG.draft
  const currency = invoice.currency ?? 'USD'
  // Billing presentation CP3: when true, hide per-line qty/rate, tiered bands,
  // and modifier lines — the customer sees description + line total only.
  const amountOnly = !!invoice.display_amount_only

  // Display "Overdue" treatment when the invoice has unpaid balance past
  // the due date — even if the row hasn't been status-flipped to
  // 'overdue' yet. This is purely cosmetic; it doesn't change what's
  // sent or what's payable.
  const overdueVisual = useMemo(() => {
    if (!invoice.due_date) return false
    if (invoice.status === 'paid' || invoice.status === 'void') return false
    return new Date(invoice.due_date) < new Date() && (invoice.amount_due ?? 0) > 0
  }, [invoice.due_date, invoice.status, invoice.amount_due])

  const payable = !!invoice.stripe_payment_link_url
    && invoice.status !== 'paid'
    && invoice.status !== 'void'

  return (
    <article style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${overdueVisual ? 'var(--danger)' : cfg.color}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace' }}>{invoice.invoice_number}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
            {cfg.label}
          </span>
          {overdueVisual && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'var(--danger-dim)', color: 'var(--danger)', border: '1px solid var(--danger-border)' }}>
              Past due
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <KeyValue label="Total" value={formatCurrency(invoice.total ?? 0, currency)} />
          <KeyValue label="Amount due" value={formatCurrency(invoice.amount_due ?? 0, currency)} bold danger={overdueVisual && (invoice.amount_due ?? 0) > 0} />
          {invoice.due_date && <KeyValue label="Due" value={formatDate(invoice.due_date)} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {payable && (
            <a href={invoice.stripe_payment_link_url!} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', background: 'var(--accent)',
                border: 'none', borderRadius: 8, color: 'var(--accent-ink)',
                fontSize: 13, fontWeight: 700, textDecoration: 'none',
              }}>
              <ExternalLink style={{ width: 12, height: 12 }} />
              Pay {formatCurrency(invoice.amount_due ?? 0, currency)}
            </a>
          )}
          {(invoice.line_items?.length ?? 0) > 0 && (
            <button type="button" onClick={() => setExpanded(e => !e)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '6px 10px', background: 'transparent',
                border: '1px solid var(--border-subtle)', borderRadius: 7,
                fontSize: 11, fontWeight: 600, color: 'var(--ink-secondary)',
                cursor: 'pointer',
              }}>
              {expanded ? <ChevronUp style={{ width: 11, height: 11 }} /> : <ChevronDown style={{ width: 11, height: 11 }} />}
              {expanded ? 'Hide details' : 'View details'}
            </button>
          )}
          <DownloadPdfButton type="invoice" id={invoice.id} />
        </div>
      </div>

      {expanded && invoice.line_items && (
        <div style={{ background: 'var(--canvas)', borderTop: '1px solid var(--border-subtle)', padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {invoice.line_items.map(li => (
              <div key={li.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{li.name}</p>
                  {li.description && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0', lineHeight: 1.4 }}>{li.description}</p>}
                  {/* Billing presentation CP3: amount-only hides the band + modifier
                      blocks (both expose qty/rate/tiers) — description + line total only. */}
                  {!amountOnly && (<>
                  {/* Arc B Slice 2: tiered/volume show the band breakdown; flat
                      (or invalid tiers → empty bands) keeps "qty × unit_price".
                      Σ(band.units × price) === li.total (pricingBands mirrors lineTotal). */}
                  {(() => {
                    const bands = pricingBands(li)
                    const labelStyle: React.CSSProperties = { fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }
                    if (bands.length === 0) {
                      return <p style={labelStyle}>{li.qty} {li.unit ?? ''} × {formatCurrency(li.unit_price, currency)}</p>
                    }
                    if (li.pricing_type === 'volume') {
                      const b = bands[0]
                      return <p style={labelStyle}>{b.units} × {formatCurrency(b.price, currency)} (volume rate)</p>
                    }
                    return <p style={labelStyle}>Tiered: {bands.map(b => `${b.units} × ${formatCurrency(b.price, currency)}`).join(', ')}</p>
                  })()}
                  {/* Arc B Slice 3: modifier lines — render for ALL pricing types
                      (incl. flat, which has no bands). The stored li.total already
                      reflects base × mult + fee, so base/bands + these reconcile. */}
                  {(() => {
                    const labelStyle: React.CSSProperties = { fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }
                    const mult = (li.price_multiplier == null || !(Number(li.price_multiplier) > 0)) ? 1 : Number(li.price_multiplier)
                    const fee = Number(li.line_fee) || 0
                    return (
                      <>
                        {mult !== 1 && <p style={labelStyle}>× {mult} surcharge</p>}
                        {fee > 0 && <p style={labelStyle}>+ {formatCurrency(fee, currency)} fee</p>}
                      </>
                    )
                  })()}
                  </>)}
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-primary)', whiteSpace: 'nowrap' }}>{formatCurrency(li.total, currency)}</span>
              </div>
            ))}
          </div>
          {(() => {
            // Arc B Slice 1: detect a minimum charge purely client-side from data
            // the portal already has — if the stored subtotal exceeds the line sum,
            // a minimum floored it. Show only THAT it was applied (the subtotal
            // already shows the floored number); never expose the minimum amount.
            // Slice 2 FIX: the line sum must use lineTotal(li) (flat/tiered/volume),
            // NOT qty×unit_price — else a tiered line makes subtotal > Σ(qty×price)
            // and this note would FALSE-fire. Cent tolerance for float noise.
            const lineSum = (invoice.line_items ?? []).reduce(
              (s, li) => s + lineTotal(li), 0)
            const minimumApplied = (invoice.subtotal ?? 0) - lineSum > 0.005
            return (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {invoice.subtotal != null && <TotalRow label="Subtotal" value={formatCurrency(invoice.subtotal, currency)} />}
            {minimumApplied && (
              <div style={{ fontSize: 10, color: 'var(--ink-tertiary)', fontStyle: 'italic', marginTop: -2 }}>
                Minimum charge applied
              </div>
            )}
            {(invoice.discount_amount ?? 0) > 0 && <TotalRow label="Discount" value={`−${formatCurrency(invoice.discount_amount!, currency)}`} />}
            {(invoice.tax_amount ?? 0) > 0 && <TotalRow label={`Tax${invoice.tax_rate ? ` (${(invoice.tax_rate * 100).toFixed(2)}%)` : ''}`} value={formatCurrency(invoice.tax_amount!, currency)} />}
            <TotalRow label="Total" value={formatCurrency(invoice.total ?? 0, currency)} />
            {(invoice.amount_paid ?? 0) > 0 && <TotalRow label="Paid" value={`−${formatCurrency(invoice.amount_paid!, currency)}`} />}
            <TotalRow label="Amount due" value={formatCurrency(invoice.amount_due ?? 0, currency)} bold />
          </div>
            )
          })()}
          {invoice.payment_terms && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Terms</p>
              <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0 }}>{invoice.payment_terms}</p>
            </div>
          )}
          {invoice.notes && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Notes</p>
              <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{invoice.notes}</p>
            </div>
          )}
          {invoice.footer_text && (
            <p style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', fontSize: 11, color: 'var(--ink-tertiary)', textAlign: 'center' }}>
              {invoice.footer_text}
            </p>
          )}
        </div>
      )}
    </article>
  )
}

function KeyValue({ label, value, bold, danger }: { label: string; value: string; bold?: boolean; danger?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 600, color: danger ? 'var(--danger)' : 'var(--ink-primary)' }}>{value}</span>
    </div>
  )
}

function TotalRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, fontSize: bold ? 13 : 12 }}>
      <span style={{ color: bold ? 'var(--ink-primary)' : 'var(--ink-tertiary)', fontWeight: bold ? 700 : 500 }}>{label}</span>
      <span style={{ color: 'var(--ink-primary)', fontWeight: bold ? 800 : 600 }}>{value}</span>
    </div>
  )
}

function DownloadPdfButton({ type, id }: { type: 'invoice' | 'quote'; id: string }) {
  const [busy, setBusy] = useState(false)
  const onClick = async () => {
    setBusy(true)
    const r = await downloadPortalPdf(type, id)
    setBusy(false)
    if (!r.ok) toast.error(r.error || 'Download failed')
  }
  return (
    <button type="button" onClick={onClick} disabled={busy}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '6px 10px', background: 'transparent',
        border: '1px solid var(--border-subtle)', borderRadius: 7,
        fontSize: 11, fontWeight: 600, color: 'var(--ink-secondary)',
        cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
      }}>
      {busy ? <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> : <Download style={{ width: 11, height: 11 }} />}
      {busy ? 'Preparing…' : 'Download PDF'}
    </button>
  )
}
